// Validação de ações. É a única fonte de verdade sobre o que é permitido:
// o reducer rejeita o que falhar aqui e getLegalActions filtra por aqui.
// Funciona tanto com o GameState completo quanto com uma PlayerView.

import {
  activePlayer,
  canAttack,
  canAct,
  canAttackBoss,
  canUseHarmony,
  canReceiveMove,
  fortifyTargets,
  hasEffect,
  hasTurnEffect,
  isParalyzed,
  isValidSet,
  isWalled,
  movableTroops,
  mustTrade,
  neighborsOf,
  ownedTerritories,
  placeableTerritories,
  powerTargets,
  symbolOf,
  truceActive,
} from './selectors';
import type { Action, PlayerId, RulesView } from './types';

const isInt = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n);

export function checkAction(s: RulesView, a: Action): string | null {
  if (!a || typeof a !== 'object') return 'bad_action';
  if (s.phase === 'over') return 'game_over';
  if (a.player !== activePlayer(s)) return 'not_your_turn';
  const me = s.players[a.player];
  if (!me) return 'bad_player';
  const owns = (t: string | undefined) => !!t && s.territories[t]?.owner === a.player;

  const pending = s.pending;
  if (pending) {
    switch (pending.kind) {
      case 'conquest':
        if (a.type !== 'moveIn') return 'pending_action';
        if (!isInt(a.count) || a.count < pending.min || a.count > pending.max) return 'bad_count';
        return null;
      case 'lootChoice':
        if (a.type !== 'chooseLoot') return 'pending_action';
        return pending.options.includes(a.kind) ? null : 'bad_choice';
      case 'bossRegion':
        if (a.type !== 'chooseBossRegion') return 'pending_action';
        return pending.regions.includes(a.region) ? null : 'bad_choice';
      case 'reaction':
        if (a.type !== 'react') return 'pending_action';
        if (typeof a.use !== 'boolean') return 'bad_action';
        if (a.use && !me.loot?.includes('luck')) return 'no_loot';
        return null;
      case 'lootOverflow':
        if (a.type === 'discardLoot') return me.loot?.includes(a.kind) ? null : 'no_loot';
        if (a.type === 'playLoot') return checkLoot(s, a);
        return 'pending_action';
      case 'heal':
        if (a.type !== 'heal') return 'pending_action';
        return owns(a.territory) ? null : 'not_owner';
      case 'hidden':
        return 'not_your_turn';
    }
  }

  switch (a.type) {
    case 'moveIn':
    case 'chooseLoot':
    case 'chooseBossRegion':
    case 'react':
    case 'heal':
    case 'discardLoot':
      return 'nothing_pending';
  }

  if (s.phase === 'setup') {
    const setup = s.setup!;
    const left = setup.remaining[a.player];
    if (a.type === 'autoSetup') return left > 0 ? null : 'bad_count';
    if (a.type !== 'place') return 'wrong_phase';
    if (!owns(a.territory)) return 'not_owner';
    const max = Math.min(s.info.ruleset.setupBatch - setup.placedThisGo, left);
    if (!isInt(a.count) || a.count < 1 || a.count > max) return 'bad_count';
    return null;
  }

  if (s.phase !== 'reinforce' && s.phase !== 'attack' && s.phase !== 'fortify') return 'wrong_phase';
  if (s.current !== a.player) return 'not_your_turn';

  if (s.phase === 'reinforce' && mustTrade(s, a.player) && a.type !== 'trade') return 'must_trade';

  if (a.type === 'place') {
    if (s.reinforcements <= 0) return 'no_reinforcements';
    if (!owns(a.territory)) return 'not_owner';
    if (!placeableTerritories(s, a.player).includes(a.territory)) return 'frozen';
    if (!isInt(a.count) || a.count < 1 || a.count > s.reinforcements) return 'bad_count';
    return null;
  }
  // Tropas extras (Contrato de Mercenários) fora da fase de reforços precisam ser colocadas antes de tudo.
  if (s.reinforcements > 0 && s.phase !== 'reinforce') return 'reinforcements_left';

  switch (a.type) {
    case 'autoSetup':
      return 'wrong_phase';
    case 'trade': {
      if (s.phase !== 'reinforce') return 'wrong_phase';
      const hand = me.cards ?? [];
      if (!Array.isArray(a.cards) || a.cards.length !== 3) return 'invalid_set';
      if (new Set(a.cards).size !== 3 || !a.cards.every((c) => hand.includes(c))) return 'invalid_set';
      return isValidSet(a.cards.map((c) => symbolOf(s, c))) ? null : 'invalid_set';
    }
    case 'endPhase':
      if (s.reinforcements > 0) return 'reinforcements_left';
      return null;
    case 'attack':
      if (s.phase !== 'attack') return 'wrong_phase';
      if (a.untilEnd !== undefined && typeof a.untilEnd !== 'boolean') return 'bad_action';
      return canAttack(s, a.player, a.from, a.to) ? null : 'cannot_attack';
    case 'attackBoss':
      if (s.phase !== 'attack') return 'wrong_phase';
      return canAttackBoss(s, a.player, a.from) ? null : 'cannot_attack';
    case 'harmony':
      if (s.phase !== 'attack') return 'wrong_phase';
      return canUseHarmony(s, a.player) ? null : 'power_unavailable';
    case 'power':
      if (s.phase !== 'attack') return 'wrong_phase';
      return powerTargets(s, a.player).includes(a.target) ? null : 'power_unavailable';
    case 'fortify': {
      if (s.phase !== 'fortify') return 'wrong_phase';
      if (!owns(a.from) || !owns(a.to) || a.from === a.to) return 'not_owner';
      if (!neighborsOf(s, a.from).includes(a.to)) return 'not_adjacent';
      if (!fortifyTargets(s, a.player, a.from).includes(a.to)) return 'blocked';
      if (!isInt(a.count) || a.count < 1 || a.count > movableTroops(s, a.from)) return 'bad_count';
      return null;
    }
    case 'playLoot':
      return checkLoot(s, a);
  }
  return 'bad_action';
}

/** Um território `t` inimigo de `p` é vizinho de algum território de `p`. */
function bordersEnemy(s: RulesView, p: PlayerId, t: string): boolean {
  return neighborsOf(s, t).some((n) => s.territories[n].owner === p);
}

/** Jogadores que podem ser alvo do Saque: têm cartas e território vizinho a um de `p`. */
export function stealVictims(s: RulesView, p: PlayerId): PlayerId[] {
  const out = new Set<PlayerId>();
  for (const t of ownedTerritories(s, p))
    for (const n of neighborsOf(s, t)) {
      const o = s.territories[n].owner;
      if (o !== p) out.add(o);
    }
  return [...out].filter((o) => {
    const pl = s.players[o] as { alive: boolean; cardCount?: number; cards?: string[] };
    return pl.alive && (pl.cards?.length ?? pl.cardCount ?? 0) > 0;
  });
}

function checkLoot(s: RulesView, a: Extract<Action, { type: 'playLoot' }>): string | null {
  const p = a.player;
  const me = s.players[p];
  if (!me.loot?.includes(a.kind)) return 'no_loot';
  if (s.current !== p) return 'loot_not_now';
  if (s.phase !== 'reinforce' && s.phase !== 'attack' && s.phase !== 'fortify') return 'loot_not_now';
  const rs = s.info.ruleset;
  const owns = (t: string | undefined) => !!t && s.territories[t]?.owner === p;
  const enemy = (t: string | undefined) => !!t && !!s.territories[t] && s.territories[t].owner !== p;
  if (truceActive(s) && (a.kind === 'strike' || a.kind === 'barrage' || a.kind === 'paralyze' || a.kind === 'steal')) return 'truce';
  switch (a.kind) {
    case 'strike':
      if (!enemy(a.territory)) return 'bad_target';
      return bordersEnemy(s, p, a.territory!) ? null : 'not_adjacent';
    case 'barrage': {
      if (!owns(a.territory)) return 'not_owner';
      const t = a.targets;
      if (!Array.isArray(t) || t.length < 1 || t.length > rs.barrageTargets || new Set(t).size !== t.length) return 'bad_target';
      const near = neighborsOf(s, a.territory!);
      return t.every((x) => enemy(x) && near.includes(x)) ? null : 'bad_target';
    }
    case 'strength':
    case 'precision':
    case 'captain':
      if (s.phase === 'fortify') return 'loot_not_now';
      return hasTurnEffect(s, p, a.kind) ? 'loot_not_now' : null;
    case 'blessing':
    case 'ward':
      if (!owns(a.territory)) return 'not_owner';
      return hasEffect(s, a.territory!, a.kind) ? 'loot_not_now' : null;
    case 'wall':
      if (!a.from || !a.to || !neighborsOf(s, a.from).includes(a.to)) return 'not_adjacent';
      return isWalled(s, a.from, a.to) ? 'loot_not_now' : null;
    case 'restore':
      if (!owns(a.territory)) return 'not_owner';
      return (s.attacked[p] ?? []).includes(a.territory!) ? null : 'bad_target';
    case 'paralyze':
      if (!enemy(a.territory)) return 'bad_target';
      return isParalyzed(s, a.territory!) ? 'loot_not_now' : null;
    case 'teleport':
      if (!owns(a.from) || !owns(a.to) || a.from === a.to) return 'not_owner';
      if (!canAct(s, a.from!) || !canReceiveMove(s, a.to!)) return 'blocked';
      if (!isInt(a.count) || a.count < 1 || a.count > movableTroops(s, a.from!)) return 'bad_count';
      return null;
    case 'reinforce':
    case 'draw':
      return null;
    case 'steal':
      return a.victim !== undefined && stealVictims(s, p).includes(a.victim) ? null : 'bad_target';
    case 'luck':
      return 'loot_not_now';
  }
  return 'bad_action';
}
